import { useRef, useState, useEffect, useCallback } from "react";

export const useScrollNavigation = (scrollAmount: number = 800) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkScrollState = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setIsScrollable(scrollWidth > clientWidth);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const checkScrollStateDebounced = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      checkScrollState();
    }, 150);
  }, [checkScrollState]);

  useEffect(() => {
    checkScrollState();
    window.addEventListener("resize", checkScrollState);
    return () => {
      window.removeEventListener("resize", checkScrollState);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [checkScrollState]);

  const scrollTo = useCallback(
    (direction: "left" | "right") => {
      if (containerRef.current) {
        containerRef.current.scrollBy({
          left: direction === "left" ? -scrollAmount : scrollAmount,
          behavior: "smooth",
        });
      }
    },
    [scrollAmount]
  );

  return {
    containerRef,
    isScrollable,
    canScrollLeft,
    canScrollRight,
    scrollTo,
    checkScrollState: checkScrollStateDebounced,
  };
};
